import DOMPurify from 'dompurify';

// Tags the rich-text editor can emit. Anything else is stripped.
const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's',
  'ul', 'ol', 'li', 'a', 'blockquote', 'code', 'pre', 'span',
];

const ALLOWED_ATTR = ['href', 'target', 'rel'];

// Force every surviving link to open safely: no opener access, no javascript: URI
// (DOMPurify already drops the scheme, but the target/rel pair still matters).
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Sanitize untrusted HTML for use with dangerouslySetInnerHTML.
 * Every such sink in the app must route through this.
 */
export function renderRichText(html?: string | null): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Block data:/javascript: payloads smuggled through href.
    ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel):/i,
  });
}

/**
 * Sanitize a plain-text message, converting newlines to <br />.
 * Use when the source is not rich text but still lands in an HTML sink.
 */
export function renderPlainText(text?: string | null): string {
  if (!text) return '';
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
  return escaped.replace(/\n/g, '<br />');
}

/**
 * Tailwind Preflight strips list markers (`ul { list-style: none }`) and link
 * styling (`a { color: inherit; text-decoration: inherit }`). globals.css only
 * restores them under `.tiptap`, i.e. inside the editor — so sanitized rich text
 * rendered anywhere else comes out as flat paragraphs.
 *
 * Apply this to every container that receives renderRichText() output, so what
 * the author sees while typing is what everyone sees after sending.
 *
 * Written out in full (not composed at runtime) because Tailwind scans source
 * text for literal class names.
 */
export const richTextClass = [
  '[&_p]:break-words [&_p]:[overflow-wrap:anywhere] [&_p]:[word-break:break-word]',
  '[&_strong]:font-semibold [&_b]:font-semibold',
  '[&_em]:italic [&_i]:italic',
  '[&_u]:underline [&_s]:line-through',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1',
  '[&_li]:my-0.5',
  '[&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-2 [&_a]:break-all',
  '[&_a:hover]:text-blue-800',
  '[&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:italic',
  '[&_code]:bg-black/5 [&_code]:px-1 [&_code]:rounded',
  '[&_pre]:bg-black/5 [&_pre]:p-2 [&_pre]:rounded [&_pre]:overflow-x-auto',
].join(' ')

/**
 * The composer's "Enter, then Enter to send" rule leaves the blank paragraph the
 * first Enter created. Trim trailing empty blocks so the sent message does not
 * carry a dangling blank line.
 */
export function stripTrailingEmptyBlocks(html?: string | null): string {
  if (!html) return ''
  return html.replace(/(?:<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/i, '')
}
